// Pure helpers for the AdminPalette ⌘K hook. Extracted from AdminPaletteHotkey.js so
// the LocalStorage / DOM-gating logic can be unit-tested without a
// LiveView/Phoenix harness.

export { isTypingInExternalInput } from "../utils/keyboard";

// Cmd/Ctrl+K pressed before the LiveView hook mounts is remembered here and
// replayed from AdminPaletteHotkey.mounted. The layout head installs the same
// listener so a press during the deferred app.js download is not dropped.
// Pages without #admin-palette (customers, the marketing site) are left alone,
// which keeps the device catalog's own Cmd+K.

function paletteShortcutState() {
  if (!window.__adminPalette) {
    window.__adminPalette = { ready: false, pending: false, listening: false };
  }
  return window.__adminPalette;
}

export function isAdminPaletteShortcut(event) {
  return (
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.shiftKey &&
    (event.key === "k" || event.key === "K")
  );
}

// Returns true when this press was claimed for the palette.
export function rememberAdminPaletteShortcut(event) {
  const state = paletteShortcutState();
  if (state.ready) return false;
  if (!isAdminPaletteShortcut(event)) return false;
  if (!document.getElementById("admin-palette")) return false;

  event.preventDefault();
  event.stopPropagation();
  state.pending = true;
  return true;
}

export function installEarlyAdminPaletteShortcut() {
  const state = paletteShortcutState();
  if (state.listening) return;
  state.listening = true;
  window.addEventListener("keydown", rememberAdminPaletteShortcut, true);
}

export function markAdminPaletteReady(ready) {
  paletteShortcutState().ready = ready;
}

export function takePendingAdminPaletteToggle() {
  const state = paletteShortcutState();
  if (!state.pending) return false;
  state.pending = false;
  return true;
}

export const RECENTS_KEY = "tvlabs.cmdk.recents";
export const RECENTS_MAX = 10;

// Read the recents list from LocalStorage. Returns an array of serial
// strings, most recent first. Swallows JSON / storage errors and returns []
// so the caller never has to guard.
export function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((r) => r && typeof r.serial === "string").map((r) => r.serial);
  } catch {
    return [];
  }
}

// Record a serial as the most-recent recent. Deduplicates by serial,
// preserves the existing recency order for the rest, caps at RECENTS_MAX.
// No-op for falsy serials; LocalStorage failures are swallowed.
export function writeRecent(serial) {
  if (!serial) return;
  try {
    const existing = (() => {
      try {
        const raw = window.localStorage.getItem(RECENTS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })();

    const filtered = existing.filter(
      (r) => r && typeof r.serial === "string" && r.serial !== serial,
    );
    const next = [{ serial, ts: Date.now() }, ...filtered].slice(0, RECENTS_MAX);

    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // localStorage can be disabled / full / cross-origin-blocked. Silently
    // ignore — recents are a nice-to-have.
  }
}
